<!-- シミュレーション用にリードタイムを変更するためのポップアップ -->

<script lang="ts" setup>
import SimpleSelectField from '@/components/input/SimpleSelectField.vue';
import NumberField from '@/components/input/NumberField.vue';
import { computed, onMounted, reactive, watch } from 'vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { ISelectFormDataSet } from '@/coreDomainsModule/table';
import {
  STEP_OF_CHANGE_DATE,
  STEP_OF_CHANGE_MONTH,
  MIN_OF_CHANGE_DATE,
  MAX_OF_CHANGE_DATE,
  UNIT_OF_DAY,
  UNIT_OF_MONTH_SIMPLE,
  UNIT_OF_WEEK_SIMPLE,
} from '@/coreDomainsModule/constants';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { DateUnitType } from '@/coreDomainsModule/date';
import { i18n } from '@/i18n';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';

const t = i18n.t;

// props
const props = defineProps<{
  popupState: boolean;
  leadTimeSet: ISelectFormDataSet;
}>();

// emits
const emits = defineEmits<{
  (e: 'onClose'): void;
  (e: 'onConfirm', leadTimeSet: ISelectFormDataSet): void;
  (e: 'onConfirmedInvalidValue'): void;
  (e: 'onUpdateLeadTime', leadTimeValue: number, leadTimeUnit: DateUnitType): void;
}>();

// イベントフック
onMounted(() => {
  state.popupState = props.popupState;
  updateInfo(props.leadTimeSet);
});

watch(
  () => props.popupState,
  (newVal) => {
    state.popupState = newVal;
    if (state.popupState) {
      updateInfo(props.leadTimeSet);
    }
  }
);

watch(
  () => props.leadTimeSet,
  (newVal) => {
    updateInfo(newVal);
  }
);

// computed
// 数値入力のステップ
const stepOfNumberField = computed(() => {
  switch (state.selectedDateUnit) {
    case 'month':
      return STEP_OF_CHANGE_MONTH;
    default:
      return STEP_OF_CHANGE_DATE;
  }
});

// 変数
const state = reactive<{
  popupState: boolean;
  displayNumber: number;
  selectedDateUnit: DateUnitType;
}>({
  popupState: false,
  displayNumber: 0,
  selectedDateUnit: 'day',
});

// ドロップダウンの選択肢
const options = [
  { label: UNIT_OF_DAY, value: 'day' },
  { label: UNIT_OF_WEEK_SIMPLE, value: 'week' },
  { label: UNIT_OF_MONTH_SIMPLE, value: 'month' },
];

// 関数

/**
 * 表示用のデータを更新する
 * @param value - ポップアップの表示に必要な情報
 */
const updateInfo = (value: ISelectFormDataSet) => {
  if (value === undefined) {
    return;
  }
  if (typeof value.selectedUnit === 'string') {
    if (value.displayData) {
      state.displayNumber = convertLeadTimeByUnit(value.displayData, value.selectedUnit);
    }
    const selectedDateUnit = DateService.convertToIDateUnit(value.selectedUnit);
    if (selectedDateUnit !== 'unknown') {
      state.selectedDateUnit = selectedDateUnit;
    } else {
      // NOTO: 単位情報が'unknown'だったらerror出しとく
      const error = new Error(`Error: stdInventory unit is 'unknown'`);
      console.error(error);
    }
  }
};

/**
 * 数値入力の値が変更されたときの処理
 * @param displayNumber - 数値入力の値
 */
const onChangeNumber = (displayNumber) => {
  if (displayNumber.isValidNumber({})) {
    state.displayNumber = displayNumber;
  }
};

/**
 * 単位の選択肢が変更されたときの処理
 * @param unit - 選択された単位
 */
const onChangeDateUnit = (unit) => {
  if (unit !== undefined) {
    state.selectedDateUnit = unit;
  }
};

/**
 * 確定ボタンが押されたときの処理
 */
const onConfirm = () => {
  // 必要に応じて小数点以下を切り捨てる
  const displayData = DateService.roundDisplayDate(
    state.displayNumber.clamp(MIN_OF_CHANGE_DATE, MAX_OF_CHANGE_DATE),
    state.selectedDateUnit
  );
  emits('onConfirm', {
    displayData: displayData,
    selectedUnit: state.selectedDateUnit,
  });
};

/**
 * DB上のリードタイム情報を更新する
 */
const onUpdateLeadTime = () => {
  // 必要に応じて小数点以下を切り捨てる
  const value = DateService.roundDisplayDate(state.displayNumber.clamp(MIN_OF_CHANGE_DATE, MAX_OF_CHANGE_DATE), state.selectedDateUnit);
  emits('onUpdateLeadTime', value, state.selectedDateUnit);
};
</script>

<template>
  <div class="main-area">
    <div v-show="state.popupState" class="outside-area" @click="emits('onClose')" />
    <div v-if="state.popupState" class="main-area__main-content main-content">
      <div class="main-content__content-container content-container">
        <div class="content-container__header">
          <div class="content-container__title">{{ t('リードタイム') }}</div>
          <!-- DB上のリードタイム情報を更新する -->
          <TextButton :button-style="'btn-secondary'" :size="'btn-ss'" class="content-container__update-default" @click="onUpdateLeadTime">
            {{ t('デフォルト値更新:1') }}<br />{{ t('デフォルト値更新:2') }}
          </TextButton>
        </div>
        <div class="content-container__edit-field-container edit-field-container">
          <div class="edit-field-container__content">
            <NumberField
              :label-square="false"
              data-type="value"
              :input-value="state.displayNumber"
              :step="stepOfNumberField"
              @change="onChangeNumber"
            />
          </div>
          <SimpleSelectField
            class="edit-field-container__content"
            :options="options"
            :selected-value="state.selectedDateUnit"
            @update:model-value="onChangeDateUnit"
          />
        </div>
        <div class="content-container__button-container button-container">
          <TextButton :button-style="'btn-secondary'" :size="'btn-ss'" class="button-container__button" @click="emits('onClose')">
            {{ t('キャンセル') }}
          </TextButton>
          <TextButton :button-style="'btn-primary'" :size="'btn-ss'" class="button-container__button" @click="onConfirm">
            {{ t('確定') }}
          </TextButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-area {
  &__main-content {
    position: relative;
    z-index: 100;
  }
}
.outside-area {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: $transparency0;
}
.main-content {
  border-radius: 8px;
  border-width: 1px;
  border-color: $border;
  background-color: $semanticWhite;
  box-shadow: $boxShadowM;
  &__content-container {
    margin: 16px;
  }
}
.content-container {
  display: grid;
  grid-template-rows: repeat(3, minmax(20px, 1fr));
  gap: 12px;
  &__title {
    font-size: $fontSizeM;
    font-weight: bold;
    text-align: left;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    justify-content: space-between;
    gap: 8px;
  }

  &__update-default {
    height: auto;
    padding: 4px 8px;
  }
}
.edit-field-container {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 8px;
  align-items: center;
  &__content {
    width: 72px;
  }
}
.button-container {
  display: flex;
  justify-content: space-between;
}
</style>
